import { stringify } from 'query-string'
import { fetchUtils } from 'ra-core'
import request from './request'

const collectionResponse = (response) => {
  const body = response.data
  if (Array.isArray(body)) return { data: body, total: body.length }
  return {
    data: body.items || [],
    total: body.totalItems ?? body.items?.length ?? 0,
  }
}

const normalizeTournamentLevel = (record, fallbackId) => {
  const points = Array.isArray(record?.rating_points)
    ? record.rating_points
    : []
  return {
    ...record,
    id: record?.id ?? record?.tournament_level_id ?? fallbackId,
    tournament_level_id:
      record?.tournament_level_id ?? record?.id ?? fallbackId,
    tournament_level_title:
      record?.tournament_level_title ?? record?.title ?? '',
    rating_points: points.map((p) => ({
      id: p.id,
      points: Number(p.points) || 0,
      place: Number(p.place) || 1,
      without_fights: Boolean(p.without_fights),
    })),
  }
}

export default (apiUrl) => ({
  getList: async (resource, params) => {
    const { page, perPage } = params?.pagination || { page: 1, perPage: 20 }
    const { field, order } = params?.sort || { field: 'id', order: 'ASC' }
    const query = { page, limit: perPage, ...params.filter, deleted: false }
    if (order && field) query[`order[${field}]`] = order
    const response = await request({
      url: `${apiUrl}/${resource}?${stringify(query || '')}`,
    })
    return collectionResponse(response)
  },

  getOne: async (resource, params) => {
    if (resource === 'students') resource = 'users'
    const endpoint =
      resource === 'tournament_levels' ? 'rating_points' : resource
    const response = await request({
      url: `${apiUrl}/${endpoint}/${params.id}`,
    })
    return {
      data:
        resource === 'tournament_levels'
          ? normalizeTournamentLevel(response.data, params.id)
          : response.data,
    }
  },

  getMany: async (resource, params) => {
    const items = []
    for (const id of params.ids) {
      const response = await request({ url: `${apiUrl}/${resource}/${id}` })
      items.push(response.data)
    }
    return { data: items }
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination
    const query = {
      ...fetchUtils.flattenObject(params.filter),
      page,
      limit: perPage,
    }
    const response = await request({
      url: `${apiUrl}/${resource}?${stringify(query)}`,
    })
    return collectionResponse(response)
  },

  update: async (resource, params) => {
    const endpoint =
      resource === 'tournament_levels' ? 'rating_points' : resource
    const response = await request({
      url: `${apiUrl}/${endpoint}/${params.id}`,
      method: 'PUT',
      data: params.data,
    })
    return {
      data: {
        ...params.data,
        ...response.data,
        id: response.data?.id || params.id,
      },
    }
  },

  updateMany: async (resource, params) => {
    const endpoint =
      resource === 'tournament_levels' ? 'rating_points' : resource
    const responses = await Promise.all(
      params.ids.map((id) =>
        request({
          url: `${apiUrl}/${endpoint}/${id}`,
          method: 'PUT',
          data: params.data,
        })
      )
    )
    return {
      data: responses.map(({ data }, index) => data?.id || params.ids[index]),
    }
  },

  create: async (resource, params) => {
    const response = await request({
      url: `${apiUrl}/${resource}`,
      method: 'POST',
      data: params.data,
    })
    return { data: { ...params.data, ...response.data, id: response.data?.id } }
  },

  delete: async (resource, params) => {
    const response = await request({
      url: `${apiUrl}/${resource}/${params.id}`,
      method: 'DELETE',
    })
    return { data: response.data || { id: params.id } }
  },

  deleteMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        request({ url: `${apiUrl}/${resource}/${id}`, method: 'DELETE' })
      )
    )
    return { data: { ids: params.ids } }
  },
})
